import type { EdgelessRootService } from '@blocksuite/blocks';
import { NoteDisplayMode } from '@blocksuite/blocks';
import type { SerializedXYWH } from '@blocksuite/global/utils';
import { serializeXYWH } from '@blocksuite/global/utils';
import type { Doc } from '@blocksuite/store';
import { nanoid } from '@blocksuite/store';

import { getRandomPastelColor } from '../../../../components/blocksuite/block-suite-editor/utils';

export type QuizNodeType = 'question' | 'answer' | 'explanation';

export interface QuizQuestionData {
  question: string;
  options?: string[];
  answer?: string;
  explanation?: string;
  keywords?: string;
  imageKeywords?: string;
}

export interface QuizNode {
  id: string;
  text: string;
  type: QuizNodeType;
  options?: string[];
  answer?: string;
  explanation?: string;
  xywh?: SerializedXYWH;
  imageKeywords?: string;
  meta?: {
    index?: number;
    style?: {
      background: string;
      textColor: string;
      fontSize: number;
      padding: [number, number];
      radius: number;
      strokeWidth: number;
      strokeColor: string;
      filled: boolean;
    };
  };
}

export interface QuizTemplate {
  blocks: QuizNode[];
  questions?: QuizNode[];
  meta: {
    type: string;
    title: string;
    theme?: QuizTheme;
  };
}

export interface QuizTheme {
  background?: string;
  cardColors?: string[];
  textColor?: string;
  spacing?: {
    horizontal: number;
    vertical: number;
  };
  fontSize?: number;
  padding?: [number, number];
  radius?: number;
  strokeWidth?: number;
  strokeColor?: string;
}

export const defaultTheme: Required<QuizTheme> = {
  background: '--affine-background-primary-color',
  cardColors: [
    '--affine-palette-shape-yellow',
    '--affine-palette-shape-blue',
    '--affine-palette-shape-green',
    '--affine-palette-shape-pink',
  ],
  textColor: '--affine-text-primary-color',
  spacing: {
    horizontal: 20,
    vertical: 20,
  },
  fontSize: 16,
  padding: [16, 16],
  radius: 8,
  strokeWidth: 1,
  strokeColor: 'var(--affine-border-color)',
};

export function createQuizNode(
  text: string,
  type: QuizNodeType,
  options: string[] | undefined,
  answer: string | undefined,
  explanation: string | undefined,
  index: number,
  theme: Required<QuizTheme>,
  imageKeywords?: string,
  xywh?: SerializedXYWH
): QuizNode {
  return {
    id: nanoid(),
    text,
    type,
    options,
    answer,
    explanation,
    imageKeywords,
    xywh,
    meta: {
      index,
      style: {
        background: theme.cardColors[index % theme.cardColors.length] ?? theme.cardColors[0] ?? '#FFFFFF',
        textColor: theme.textColor,
        fontSize: theme.fontSize,
        padding: theme.padding,
        radius: theme.radius,
        strokeWidth: theme.strokeWidth,
        strokeColor: theme.strokeColor,
        filled: true,
      },
    },
  };
}

export function createQuizTemplate(data: {
  response: string;
  title?: string;
  theme?: Partial<QuizTheme>;
}): QuizTemplate {
  const theme: Required<QuizTheme> = {
    ...defaultTheme,
    ...data.theme,
  };

  try {
    const quiz = JSON.parse(data.response.trim()) as { questions?: QuizQuestionData[]; title?: string };
    if (!quiz?.questions) {
      throw new Error('Invalid quiz data: missing questions array');
    }

    const blocks: QuizNode[] = [];
    quiz.questions.forEach((q: QuizQuestionData, index: number) => {
      if (!q.question) {
        throw new Error(
          `Invalid question at index ${index}: missing question text`
        );
      }

      blocks.push(
        createQuizNode(
          q.question,
          'question',
          q.options,
          q.answer,
          q.explanation,
          index,
          theme,
          q.imageKeywords
        )
      );
    });

    return {
      blocks,
      questions: blocks,
      meta: {
        type: 'quiz',
        title: data.title || quiz.title || 'Quiz',
        theme,
      },
    };
  } catch (error) {
    console.error('Failed to parse quiz data:', error);
    console.error('Raw response:', data.response);
    throw error;
  }
}

export function createQuizCardStack(
  edgelessService: EdgelessRootService,
  quiz: QuizTemplate,
  doc: Doc & { Text: new (text: string) => unknown; root: { id: string } }
): string[] {
  const cardIds: string[] = [];
  const startX = 100;
  const startY = 100;
  const cardWidth = 600;
  const cardHeight = 400;
  const stackOffset = 20;

  // Create a frame to contain all cards
  edgelessService.addBlock(
    'affine:frame',
    {
      title: new doc.Text(quiz.meta.title || 'Hey, quick question!'),
      xywh: serializeXYWH(
        startX - 20,
        startY - 20,
        cardWidth + 60,
        cardHeight + stackOffset
      ),
    },
    edgelessService.surface
  );

  quiz.blocks.forEach((block, i) => {
    const cardX = startX + i * stackOffset;
    const cardY = startY + i * stackOffset;

    const cardId = edgelessService.addBlock(
      'affine:note',
      {
        xywh: serializeXYWH(cardX, cardY, cardWidth, cardHeight),
        background: getRandomPastelColor(),
        displayMode: NoteDisplayMode.EdgelessOnly,
        edgeless: {
          style: {
            borderRadius: 24,
            shadowType: '--affine-note-shadow-sticker',
            borderSize: 0,
          },
        },
        index: edgelessService.layer.generateIndex('affine:note'),
      },
      doc.root?.id
    );

    // Add title
    const titleId = edgelessService.addBlock(
      'affine:paragraph',
      {
        text: new doc.Text(`Question ${i + 1}`),
        type: 'h1',
      },
      cardId
    );

    edgelessService.updateElement(titleId, {
      style: {
        color: '--affine-palette-line-blue',
        fontSize: '24px',
        fontWeight: 'bold',
        textAlign: 'center',
      },
    });

    // Add question text
    const questionId = edgelessService.addBlock(
      'affine:paragraph',
      {
        text: new doc.Text(block.text),
        type: 'text',
      },
      cardId
    );

    edgelessService.updateElement(questionId, {
      style: {
        fontSize: '22px',
        fontWeight: 'medium',
        marginTop: '20px',
        marginBottom: '20px',
      },
    });

    // Add options if present
    if (block.options) {
      block.options.forEach((option, _index) => {
        const isCorrect = option === block.answer;
        const optionId = edgelessService.addBlock(
          'affine:list',
          {
            text: new doc.Text(option),
            // checked: isCorrect,
            checked: false,
            type: 'todo',
          },
          cardId
        );

        edgelessService.updateElement(optionId, {
          style: {
            fontSize: '18px',
            marginLeft: '20px',
            color: isCorrect ? '--affine-palette-line-green' : undefined,
          },
        });
      });
    }

    cardIds.push(cardId);
  });

  // Group all cards together
  edgelessService.addElement('group', {
    children: cardIds,
    title: quiz.meta.title || 'Quiz Card Group',
    index: edgelessService.layer.generateIndex('group'),
  });

  return cardIds;
}
